import * as React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { VirtualBrand, VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { getVirtualBrand } from '~/MedicinalBrandsAnalytics/VirtualBrand';
import { selectVirtualPack } from '~/MedicinalBrandsAnalytics/VirtualBrand/BrandDetail';
import { selectVirtualBrand } from '~/MedicinalBrandsAnalytics/VirtualBrand/BrandDetailContainer';
import { ApplicationState, ThunkDispatch } from '~/Store';

type StoredResult = {
	stored: true;
	virtualBrand: VirtualBrand;
	virtualPack: VirtualPack;
	fetching: false;
};

type FetchingResult = { stored: false; virtualBrand: null; virtualPack: null; fetching: true };
type UnabledToFetchResult = {
	stored: false;
	virtualBrand: null;
	virtualPack: null;
	fetching: false;
};

export const useFetchOrGetVirtualBrand = (
	virtualBrandId: string,
	virtualPackId: string
): FetchingResult | StoredResult | UnabledToFetchResult => {
	const storedVirtualBrand = useSelector(({ medicinalBrandsAnalyticsVirtualBrand }: ApplicationState) =>
		selectVirtualBrand(virtualBrandId, medicinalBrandsAnalyticsVirtualBrand)
	);

	const dispatch = useDispatch<ThunkDispatch<any>>();
	const store = useStore();
	const [fetchState, setFetchedBrand] = React.useState<{
		fetching: boolean;
		fetchedBrand: VirtualBrand | null;
	}>({
		fetching: false,
		fetchedBrand: storedVirtualBrand,
	});

	React.useEffect(() => {
		setFetchedBrand((prev) => ({
			...prev,
			fetching: true,
		}));

		if (!storedVirtualBrand) {
			dispatch(getVirtualBrand(virtualBrandId)).then(() => {
				// TODO: Add correct type
				const state: any = store.getState();

				const responseBrand = selectVirtualBrand(virtualBrandId, state.medicinalBrandsAnalyticsVirtualBrand);
				setFetchedBrand({ fetching: false, fetchedBrand: responseBrand });
			});
		}
	}, [virtualBrandId, dispatch, store, storedVirtualBrand, setFetchedBrand]);

	const storedVirtualPack = storedVirtualBrand
		? selectVirtualPack(storedVirtualBrand.virtualPacks, virtualPackId)
		: null;

	if (storedVirtualBrand && storedVirtualPack) {
		return {
			stored: true,
			fetching: false,
			virtualBrand: storedVirtualBrand,
			virtualPack: storedVirtualPack,
		};
	}

	const fetchedVirtualPack = fetchState.fetchedBrand
		? selectVirtualPack(fetchState.fetchedBrand.virtualPacks, virtualPackId)
		: null;
	if (fetchState.fetchedBrand && fetchedVirtualPack) {
		return {
			stored: true,
			fetching: false,
			virtualBrand: fetchState.fetchedBrand,
			virtualPack: fetchedVirtualPack,
		};
	}

	if (fetchState.fetching) {
		return {
			stored: false,
			fetching: true,
			virtualBrand: null,
			virtualPack: null,
		};
	}

	return {
		stored: false,
		fetching: false,
		virtualBrand: null,
		virtualPack: null,
	};
};
